import { StandardEvents } from '@shopify/events';

/**
 * Showcase's add-button feedback (theme.js applyAjaxToProductForm): from the
 * submit the product page's add button reads "Adding", once the line is in a
 * tick and "Added to cart", and it takes no clicks until it is back to "Add
 * to Cart". Showcase draws those labels in the button's background colour,
 * black on black with BIRROT's settings, a blank button for 4s; here they
 * are in its text colour (birrot-product.css), and the button is back when
 * the cart drawer closes or 4s after the add. A failed add lets go
 * at once; Horizon shows its error.
 *
 * The hold starts on Horizon's add event, which its submit handler
 * dispatches synchronously and only for an add it will send: a form the
 * browser finds invalid never submits, and a quantity over the maximum
 * returns before the event. It ends when the event's promise settles,
 * however long the request takes. Horizon owns `disabled` (a sold-out
 * variant), so the button is not disabled: a click on a held button,
 * including the one Enter in the form makes, is stopped before Horizon's
 * handlers see it.
 *
 * Only the product template's main section: quick add closes its modal
 * on add, and a featured product elsewhere does not hold its button.
 * Labels and markup: snippets/birrot-product-form.liquid.
 */

/** How long "Added to cart" stays when the cart drawer is left open (theme.js's 4000). */
const ADDED_HOLD = 4000;

const MAIN_SECTION = ".shopify-section[id$='__main']";

/** @type {WeakMap<HTMLElement, number>} */
const releaseTimers = new WeakMap();

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null} The main product form's add button, if `target` is in it.
 */
function mainButton(target) {
  if (!(target instanceof Element) || !target.closest(MAIN_SECTION)) return null;
  const button = target.closest('product-form-component')?.querySelector('.add-to-cart-button');
  return button instanceof HTMLElement ? button : null;
}

/**
 * @param {HTMLElement} button
 * @param {'adding' | 'added'} state
 */
function show(button, state) {
  clearTimeout(releaseTimers.get(button));

  const template = document.getElementById('birrot-atc-status');
  if (!(template instanceof HTMLTemplateElement)) return;

  let status = button.querySelector('.birrot-atc-status');
  if (!status) {
    status = /** @type {Element} */ (template.content.firstElementChild?.cloneNode(true));
    button.append(status);
  }
  status
    .querySelector('.birrot-atc-status__text')
    ?.replaceChildren(template.dataset[state === 'adding' ? 'addingLabel' : 'addedLabel'] ?? '');
  button.dataset.birrotAdding = state;
}

/**
 * @param {HTMLElement} button
 * @param {number} [delay]
 */
function release(button, delay = 0) {
  clearTimeout(releaseTimers.get(button));
  releaseTimers.set(
    button,
    setTimeout(() => {
      delete button.dataset.birrotAdding;
      button.querySelector('.birrot-atc-status')?.remove();
    }, delay)
  );
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const { action, promise } = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
  if (action !== 'add') return;

  const button = mainButton(event.target);
  if (!button) return;

  if (!promise) return;
  show(button, 'adding');

  promise
    .then(({ detail }) => {
      if (detail?.didError) {
        release(button);
        return;
      }
      show(button, 'added');
      release(button, ADDED_HOLD);
    })
    .catch(() => release(button));
});

document.addEventListener(
  'click',
  (event) => {
    const button = mainButton(event.target);
    if (!button?.hasAttribute('data-birrot-adding') || !button.contains(/** @type {Node} */ (event.target))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  { capture: true }
);

// The cart drawer closed: theme-drawer's own event (bubbles), not the
// dialog's `close`, which also fires while the drawer stays open (it closes
// and re-shows its dialog across 990px and to bring itself to the front).
document.addEventListener('theme-drawer:close', (event) => {
  if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
  for (const button of document.querySelectorAll(`${MAIN_SECTION} .add-to-cart-button[data-birrot-adding='added']`)) {
    if (button instanceof HTMLElement) release(button);
  }
});
